import { ChartOptions, ScriptableContext, TooltipItem } from 'chart.js'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants.ts'
import { isUnfinishedPeriod } from './utils.ts'
import { createChartDataArray, formatChartDateLabel } from 'utils/chartjs.ts'
import { ExternalTooltipContext } from 'components/charts/types.ts'
import { capitalizeFirstLetter, formatAmount } from 'utils/format.ts'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'
import { GetPriceHistoryChartDatasets, PriceHistoryBarDataset, PriceHistoryChartOptions } from './types.ts'
import { Granularity } from 'types/contracts.ts'

/**
 * Get price history chart options
 * @param {PriceHistoryChartOptions} params
 */
export const getPriceHistoryChartOptions = ({
  granularity,
  handleHoverDataPoint,
  isMobile,
  labels
}: PriceHistoryChartOptions): ChartOptions<'bar'> => {
  const defaults = getBarChartDefaults({ isMobile })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    // Keeps the hovered bar highlight in sync with the tooltip, which triggers on the nearest bar of the hovered column
    interaction: {
      intersect: false,
      mode: 'index'
    },
    layout: {
      padding: {
        ...defaults.layout.padding,
        left: 5,
        right: 5
      }
    },
    scales: {
      y: {
        ...y,
        ticks: {
          ...y.ticks,
          callback: (value: string | number) => formatAmount(Number(value), { fractionDigits: 2 })
        }
      },
      x: {
        ...x,
        ticks: {
          ...x.ticks,
          callback: (value: string | number) => formatChartDateLabel(labels[Number(value)], granularity)
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: '€ / kWh'
        }
      },
      tooltip: {
        ...TOOLTIP_CONFIG,
        external: (context: ExternalTooltipContext<'bar'>) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({ context, totalColumns: labels.length })
        },
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) =>
            capitalizeFirstLetter(
              formatChartDateLabel(
                context?.[0]?.label,
                granularity,
                granularity !== Granularity.HOUR && granularity !== Granularity.QUARTER_HOUR
              )
            )
        }
      }
    }
  }
}

/**
 * Get the price history chart datasets
 * @param {GetPriceHistoryChartDatasets} params
 * @returns {PriceHistoryBarDataset[]}
 */
export const getPriceHistoryChartDatasets = ({
  labels,
  data,
  granularity,
  isMobile
}: GetPriceHistoryChartDatasets): PriceHistoryBarDataset[] => {
  const isUnfinished = ({ dataIndex }: ScriptableContext<'bar'>) =>
    granularity === Granularity.MONTH && isUnfinishedPeriod(labels[dataIndex], granularity)

  return [
    {
      type: 'bar',
      backgroundColor: (context) => (isUnfinished(context) ? CHART_COLORS['grey-subdued'] : CHART_COLORS.grey),
      hoverBackgroundColor: CHART_COLORS.dark,
      data: createChartDataArray(
        labels.length,
        data.map(({ price }) => price ?? null)
      ),
      borderSkipped: 'start',
      maxBarThickness: 28,
      borderRadius: isMobile ? 2 : 4
    }
  ]
}
